import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    Button,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    OverlayDrawer,
    tokens,
    useRestoreFocusSource,
} from "@fluentui/react-components";
import { ArrowCircleLeft32Regular } from "@fluentui/react-icons";

interface NewformaPanelProps extends LocalizeContextProps {
    isOpen: boolean;
    title: React.ReactNode;
    closeButtonDisabled?: boolean;
    footer: React.ReactNode;
    onOpen?: () => void | Promise<void>;
    onClose: () => void;
    children: React.ReactNode;
    testid: string;
    /** When true, the panel stays mounted in the DOM when closed (state is preserved). */
    keepMounted?: boolean;
}

const panelStyles = makeStyles({
    header: {
        paddingLeft: "16px",
        display: "flex",
        flexFlow: "row nowrap",
        alignItems: "start",
        gap: ".2em",
        "& h2": {
            fontWeight: tokens.fontWeightBold,
            fontSize: tokens.fontSizeBase400,
        },
    },
});

export const NewformaPanel = withLocalize(
    ({
        isOpen,
        title,
        footer,
        closeButtonDisabled,
        onOpen,
        onClose,
        children,
        translate,
        testid: testid,
        keepMounted = false,
    }: NewformaPanelProps) => {
        if (!isOpen && !keepMounted) {
            // Early return unmounts the Drawer
            return null;
        }
        // This may be needed later
        // const restoreFocusTargetAttributes = useRestoreFocusTarget();
        const restoreFocusSourceAttributes = useRestoreFocusSource();
        const styles = panelStyles();

        React.useEffect(() => {
            if (isOpen) {
                void onOpen?.();
            }
        }, [isOpen, onOpen]);

        return (
            <OverlayDrawer
                data-testid={testid ?? ""}
                {...restoreFocusSourceAttributes}
                mountNode={document.querySelector(".fui-FluentProvider") ?? document.body} // Must be inside fluent provider or theme won't work
                position="end"
                size="full"
                open={isOpen}
                modalType="non-modal"
                surfaceMotion={null}
                style={{ zIndex: 5 }}
                unmountOnClose={!keepMounted}
            >
                <DrawerHeader className={styles.header} data-testid={`${testid}-header`}>
                    <Button
                        data-testid={`${testid}-header-close-button`}
                        appearance="subtle"
                        disabled={closeButtonDisabled}
                        title={translate("FLUENT9.CLOSE") as string}
                        aria-label={translate("FLUENT9.CLOSE") as string}
                        icon={<ArrowCircleLeft32Regular style={{ color: tokens.colorBrandForeground1 }} />}
                        onClick={onClose}
                    />
                    <DrawerHeaderTitle>{title}</DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody data-testid={`${testid}-body`}>{children}</DrawerBody>
                <DrawerFooter data-testid={`${testid}-footer`}>{footer}</DrawerFooter>
            </OverlayDrawer>
        );
    }
);
